import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import type { TrackEvent, LapData } from "@/types/generated";
import { Button } from "../ui/button";
import {
  ToggleLeft,
  ToggleRight,
  AlertCircle,
  Trash2,
  Save,
  Download,
  MoreHorizontal,
  ExternalLink,
} from "lucide-react";
import { LapTime } from "../ui/lap-time";
import { TextHighlighter } from "../ui/text-highlighter";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { PersonalBestBadge } from "../PersonalBestBadge";
import { toast } from "sonner";
import { router } from "@inertiajs/react";
import { cn } from "@/lib/utils";

interface LapTimesTableProps {
  trackEvent: TrackEvent;
  lapData: LapData[];
}

export function LapTimesTable({ trackEvent, lapData }: LapTimesTableProps) {
  const [gapMode, setGapMode] = useState<"session" | "own">("session");
  const [openPopoverId, setOpenPopoverId] = useState<number | null>(null);
  const [editingAnnotation, setEditingAnnotation] = useState<
    Record<number, string>
  >({});

  const validLaps = lapData.filter((lap) => lap.is_valid);

  const userBestLapMs = trackEvent.best_lap_time_ms;
  const sessionFastestLapMs = trackEvent.session_fastest_lap_ms;
  const hasMultiDriverData =
    sessionFastestLapMs &&
    userBestLapMs &&
    sessionFastestLapMs !== userBestLapMs;
  const referenceTimeMs =
    gapMode === "session"
      ? sessionFastestLapMs || userBestLapMs || null
      : userBestLapMs || null;

  const userFastestLap =
    validLaps.length > 0
      ? validLaps.reduce(
        (fastest, current) =>
          current.lap_time_ms < fastest.lap_time_ms ? current : fastest,
        validLaps[0]
      )
      : null;

  function handleOpenPopover(lapId: number, currentAnnotation?: string) {
    setOpenPopoverId(lapId);
    if (currentAnnotation && !editingAnnotation[lapId]) {
      setEditingAnnotation((prev) => ({ ...prev, [lapId]: currentAnnotation }));
    } else if (!currentAnnotation && !editingAnnotation[lapId]) {
      setEditingAnnotation((prev) => ({ ...prev, [lapId]: "" }));
    }
  }

  async function handleSaveAnnotation(lapId: number) {
    const annotation = editingAnnotation[lapId];
    if (!annotation || annotation.trim().length === 0) {
      toast.error("Error", { description: "Annotation cannot be empty" });
      return;
    }
    try {
      const response = await fetch(
        `/event/${trackEvent.id}/lap/${lapId}/annotation`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token":
              document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
                ?.content || "",
          },
          body: JSON.stringify({ annotation }),
        }
      );
      const data = await response.json();
      if (data.success) {
        toast.success("Annotation saved!");
        setOpenPopoverId(null);
        setEditingAnnotation((prev) => {
          const updated = { ...prev };
          delete updated[lapId];
          return updated;
        });
        router.reload({ preserveUrl: true });
      } else {
        toast.error("Error", {
          description: data.error || "Failed to save annotation",
        });
      }
    } catch {
      toast.error("Error", {
        description: "Failed to save annotation. Please try again.",
      });
    }
  }

  async function handleDeleteAnnotation(lapId: number) {
    try {
      const response = await fetch(
        `/event/${trackEvent.id}/lap/${lapId}/annotation`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token":
              document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
                ?.content || "",
          },
        }
      );
      const data = await response.json();
      if (data.success) {
        toast.success("Annotation deleted!");
        setOpenPopoverId(null);
        setEditingAnnotation((prev) => {
          const updated = { ...prev };
          delete updated[lapId];
          return updated;
        });
        router.reload({ preserveUrl: true });
      } else {
        toast.error("Error", {
          description: data.error || "Failed to delete annotation",
        });
      }
    } catch {
      toast.error("Error", {
        description: "Failed to delete annotation. Please try again.",
      });
    }
  }

  // Calculate max sectors
  const maxSectors = lapData.reduce((max, lap) => {
    // Check if sector_times is an array (from backend service)
    if (Array.isArray(lap.sector_times)) {
      return Math.max(max, lap.sector_times.length);
    }
    return max;
  }, 0);

  const formatSectorTime = (time: number | null | undefined) => {
    if (time === null || time === undefined) return "—";
    return time.toFixed(3);
  };

  return (
    <Card className="mb-6">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">Lap Times</CardTitle>
          <div className="flex items-center gap-3">
            {hasMultiDriverData && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() =>
                  setGapMode(gapMode === "session" ? "own" : "session")
                }
                className="text-xs"
              >
                {gapMode === "session" ? (
                  <ToggleLeft className="w-4 h-4 mr-1.5" />
                ) : (
                  <ToggleRight className="w-4 h-4 mr-1.5" />
                )}
                Gap: {gapMode === "session" ? "Session Best" : "Own Best"}
              </Button>
            )}
            <span className="text-sm text-muted-foreground">
              {validLaps.length} valid of {lapData.length} total
            </span>
            {trackEvent.can_edit && (
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <MoreHorizontal className="w-4 h-4" />
                    <span className="sr-only">Open menu</span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-40 p-1" align="end">
                  <a
                    href={`/event/${trackEvent.id}/laps.csv`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center w-full px-2 py-1.5 text-sm rounded-sm hover:bg-accent hover:text-accent-foreground cursor-pointer"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Export CSV
                  </a>
                </PopoverContent>
              </Popover>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b">
                <th className="text-left py-3 px-4 font-medium text-sm">Lap</th>
                <th className="text-left py-3 px-4 font-medium text-sm">
                  Time
                </th>
                {Array.from({ length: maxSectors }).map((_, i) => (
                  <th key={i} className="text-left py-3 px-4 font-medium text-sm">
                    S{i + 1}
                  </th>
                ))}
                <th className="text-left py-3 px-4 font-medium text-sm">Gap</th>
                <th className="text-left py-3 px-4 font-medium text-sm">PB</th>
              </tr>
            </thead>
            <tbody>
              {lapData.map((lap, index) => {
                const gapMs =
                  lap.is_valid && referenceTimeMs
                    ? lap.lap_time_ms - referenceTimeMs
                    : 0;
                const isUserBest =
                  lap.is_valid && lap.id === userFastestLap?.id;
                const annotation = lap.lap_annotations?.[0];
                const hasAnnotation = !!annotation;
                const isPopoverOpen = openPopoverId === lap.id;
                const sectors = Array.isArray(lap.sector_times) ? lap.sector_times : [];

                return (
                  <Popover
                    key={lap.id}
                    open={isPopoverOpen}
                    onOpenChange={(open) => {
                      if (open) {
                        handleOpenPopover(lap.id, annotation?.annotation);
                      } else {
                        setOpenPopoverId(null);
                        setEditingAnnotation((prev) => {
                          const updated = { ...prev };
                          delete updated[lap.id];
                          return updated;
                        });
                      }
                    }}
                  >
                    <PopoverTrigger asChild>
                      <tr
                        className={cn(
                          "border-b hover:bg-accent/50 transition-colors cursor-pointer",
                          isPopoverOpen && "bg-accent/50"
                        )}
                      >
                        <td className="py-3 px-4 font-medium">
                          {index + 1}
                          {!lap.is_valid && (
                            <AlertCircle className="ml-2 w-4 h-4 inline text-destructive" />
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <TextHighlighter
                            isHighlighted={hasAnnotation}
                            triggerType="inView"
                            direction="ltr"
                            highlightColor="rgba(253, 224, 71, 0.35)"
                            transition={{
                              type: "spring",
                              stiffness: 120,
                              damping: 18,
                            }}
                            className="px-1 -mx-1"
                          >
                            <LapTime
                              timeMs={lap.lap_time_ms}
                              variant={isUserBest ? "highlight" : "secondary"}
                              className={lap.is_valid ? "" : "opacity-40"}
                            />
                          </TextHighlighter>
                        </td>
                        {Array.from({ length: maxSectors }).map((_, i) => (
                          <td key={i} className="py-3 px-4 text-sm font-mono text-muted-foreground">
                            {formatSectorTime(sectors[i])}
                          </td>
                        ))}
                        <td className="py-3 px-4">
                          {lap.is_valid && gapMs > 0 ? (
                            <span className="text-destructive text-sm">
                              +{(gapMs / 1000).toFixed(3)}s
                            </span>
                          ) : lap.is_valid && gapMs < 0 ? (
                            <span className="text-green-600 text-sm">
                              {(gapMs / 1000).toFixed(3)}s
                            </span>
                          ) : lap.is_valid && gapMs === 0 ? (
                            <span className="text-sm font-medium">Best</span>
                          ) : (
                            <span className="text-muted-foreground text-sm">
                              —
                            </span>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          {lap.personal_best_rank && (
                            <PersonalBestBadge
                              rank={lap.personal_best_rank}
                              size="sm"
                            />
                          )}
                        </td>
                      </tr>
                    </PopoverTrigger>
                    <PopoverContent
                      align="center"
                      side="top"
                      sideOffset={8}
                      className="w-80"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <h4 className="font-medium text-sm">
                            Lap {index + 1} Annotation
                          </h4>
                          {hasAnnotation && !trackEvent.can_edit && (
                            <span className="text-xs text-muted-foreground">
                              by {annotation.user.first_name}
                            </span>
                          )}
                        </div>
                        {trackEvent.can_edit ? (
                          <div className="space-y-3">
                            <textarea
                              value={editingAnnotation[lap.id] || ""}
                              onChange={(e) =>
                                setEditingAnnotation((prev) => ({
                                  ...prev,
                                  [lap.id]: e.target.value,
                                }))
                              }
                              maxLength={150}
                              placeholder="Add a note about this lap..."
                              className="w-full h-24 px-3 py-2 text-sm border rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-ring"
                            />
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-muted-foreground">
                                {(editingAnnotation[lap.id] || "").length}/150
                              </span>
                              <div className="flex gap-2">
                                {lap.garage61_id && (
                                  <Button
                                    size="sm"
                                    variant="secondary"
                                    asChild
                                  >
                                    <a
                                      href={`https://garage61.net/app/analysis/laps/${lap.garage61_id}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="flex items-center"
                                    >
                                      <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
                                      Analyze
                                    </a>
                                  </Button>
                                )}
                                {hasAnnotation && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() =>
                                      handleDeleteAnnotation(lap.id)
                                    }
                                  >
                                    <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                                    Delete
                                  </Button>
                                )}
                                <Button
                                  size="sm"
                                  onClick={() => handleSaveAnnotation(lap.id)}
                                  disabled={!editingAnnotation[lap.id]?.trim()}
                                >
                                  <Save className="h-3.5 w-3.5 mr-1.5" />
                                  Save
                                </Button>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <p className="text-sm text-muted-foreground">
                            {annotation?.annotation || "No annotation"}
                          </p>
                        )}
                      </div>
                    </PopoverContent>
                  </Popover>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
